import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { useRealtime } from '../../hooks/useRealtime';
import {
  buildCoveNarrative,
  type BotTickPayload,
} from '../../lib/coveNarrative';
import { useCoveDensity } from '../../lib/prefs';
import { WhaleGlyph } from '../BrandMark';
import type { RealtimeEnvelope } from '../../lib/realtime';

export type OceanMode = 'hunting' | 'guarding' | 'sleeping';
export type GuardTone = 'positive' | 'negative' | 'neutral';

interface Props {
  mode: OceanMode;
  /**
   * Hunting mode fallback: 0..1 — used as whale depth before any live
   * ``score_ratio`` arrives via ``:bot`` ticks (and after the latest
   * stored score goes stale). 1 = surface, 0 = seabed.
   */
  confidence?: number;
  /**
   * Guarding mode only: trade direction. ``up`` → whale ascends to the
   * surface, ``down`` → whale dives near the seabed. Mirrors the bet
   * the user placed: "I think this goes up" → whale rises with you.
   */
  guardSide?: 'up' | 'down';
  /**
   * Guarding mode only: PnL sign. ``positive`` tints the whale green,
   * ``negative`` tints it red. ``neutral`` leaves the brand accent
   * (e.g. during the awaiting-settle window where PnL is locked).
   */
  guardTone?: GuardTone;
  /**
   * Currently-displayed PM market. When set, the tick log filters
   * ``:bot`` envelopes to this ``payload.market_id`` so the user only
   * sees decisions on the chip they're looking at, and the hunting
   * whale's depth reads the latest stored ``score_ratio`` for this
   * market. Other markets keep accumulating in the background — chip
   * switch is instant, no re-fetch.
   */
  activeMarketId?: string;
  className?: string;
}

interface Bubble {
  id: number;
  x: number;
  tint: 'neutral' | 'positive' | 'negative';
}

interface TickRow {
  id: number;
  time: string;
  body: string;
  tone: 'neutral' | 'positive' | 'negative';
  /** ``market_id|renderedBody`` — any row already in the log with the
   *  same key absorbs a repeat tick (count++), so interleaved strategies
   *  don't stack duplicate-looking lines. Keeps the log readable when
   *  ``no_signal`` ticks every ~3 s. */
  dedupeKey: string;
  /** Number of envelopes folded into this row. >1 → suffix ``×N``. */
  count: number;
  /** Monotonic recency rank — assigned on create, re-stamped on every
   *  in-place ``count++``. On overflow the row with the lowest ``seq``
   *  (the genuinely least-recently-active line) is evicted, so an
   *  in-place-refreshed row sitting near the array tail isn't dropped
   *  ahead of a staler sibling above it. */
  seq: number;
}

const MAX_BUBBLES = 14;
const MAX_TICKS = 3;
/** Hunting mode: ~1 in N global:price envelopes spawns a sonar ping
 *  (otherwise the ping flickers on every tick and becomes noise). */
const PING_EVERY_N_TICKS = 5;
/** Stored score_ratio is dropped if older than this — falls back to
 *  the ``confidence`` prop default. ~5× a typical PM tick (3s) so a
 *  brief WS hiccup doesn't sink the whale, but a real outage will. */
const SCORE_STALE_MS = 15_000;
const SCORE_GC_INTERVAL_MS = 5_000;

interface ScoreEntry {
  ratio: number;
  ts: number;
}

/**
 * Decorative ocean strip — wave surface + whale silhouette + bubble
 * trail + transient sonar ping + narrated tick log. Embedded as the
 * footer inside PriceToBeat (hunting) and ActiveTrade (guarding). The
 * intent is "the bot is alive" — every server tick produces a bubble,
 * so a long silence becomes visible immediately.
 *
 * PR-1 source: `global:price` envelopes drive bubbles + tick log; real
 * per-strategy decision events (S1 skipped because edge<floor, etc.)
 * land in a follow-up PR once the trading lane publishes them — see
 * HANDOFFS.md.
 */
export function OceanStrip({
  mode,
  confidence = 0.5,
  guardSide,
  guardTone = 'neutral',
  activeMarketId,
  className,
}: Props) {
  const { t, i18n } = useTranslation();
  const [density] = useCoveDensity();
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [ticks, setTicks] = useState<TickRow[]>([]);
  const [pingId, setPingId] = useState(0);
  const [scoreByMarket, setScoreByMarket] = useState<Record<string, ScoreEntry>>({});
  const idRef = useRef(0);
  const seqRef = useRef(0);
  const tickCountRef = useRef(0);
  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';

  const handlePriceTick = useCallback(() => {
    if (mode === 'sleeping') return;

    // Density gate. Calm: drop ~70% of envelopes so the strip is
    // unhurried. Busy: spawn an extra bubble per envelope.
    const dropProb = density === 'calm' ? 0.7 : 0;
    if (dropProb > 0 && Math.random() < dropProb) return;
    const count = density === 'busy' ? 2 : 1;

    const tint: Bubble['tint'] =
      mode === 'guarding' && guardTone !== 'neutral' ? guardTone : 'neutral';

    setBubbles((prev) => {
      const fresh = Array.from({ length: count }, () => ({
        id: idRef.current++,
        x: 8 + Math.random() * 84,
        tint,
      }));
      const next = [...prev, ...fresh];
      return next.length > MAX_BUBBLES ? next.slice(-MAX_BUBBLES) : next;
    });

    tickCountRef.current += 1;
    if (tickCountRef.current % PING_EVERY_N_TICKS === 0) {
      setPingId((p) => p + 1);
    }
  }, [density, guardTone, mode]);

  // Bubbles + sonar pulse on every oracle envelope — gives the strip
  // its "alive" cadence regardless of which market the user is on.
  useRealtime('global:price', handlePriceTick);

  // Tick log + per-market score storage from `user:{id}:bot` envelopes
  // emitted by the trading-engine (PR #330, contract in
  // ``polybot_trading/bot_tick.py``). Two outputs from one stream:
  //   - latest ``score_ratio`` cached per market_id (drives hunting
  //     whale depth when this market is active),
  //   - narrative line for the bottom log, filtered to the active
  //     chip so a non-BTC tick doesn't muddy a BTC-focused view.
  useRealtime(
    // Match only the per-user bot channel — narrow to ``user:{id}:bot``
    // so a future ``global:bot`` (if ever added for platform-wide
    // signals) won't accidentally feed into the per-market storage.
    /^user:[^:]+:bot$/,
    (env: RealtimeEnvelope) => {
      if (env.type !== 'tick') return;
      const payload = env.payload as BotTickPayload & {
        score_ratio?: number;
      };

      // Store score_ratio regardless of active market — chip switch
      // can then read whatever's freshest without waiting for the
      // next tick on that market. Stamp with CLIENT wall clock, not
      // ``env.ts`` — staleness is a client-side question ("how long
      // since I last received data") and worker clock skew (NTP drift,
      // container TZ bugs) would otherwise mark fresh envelopes as
      // stale on arrival and silently freeze the whale at fallback.
      if (
        typeof payload.score_ratio === 'number' &&
        Number.isFinite(payload.score_ratio) &&
        payload.market_id
      ) {
        const ratio = Math.max(0, Math.min(1, payload.score_ratio));
        const marketId = payload.market_id;
        setScoreByMarket((prev) => ({
          ...prev,
          [marketId]: { ratio, ts: Date.now() },
        }));
      }

      // Narrative is per-market — drop envelopes for other markets
      // so the log shows only the chip the user is looking at. When
      // ``activeMarketId`` is unset (no PM market context), accept
      // all envelopes so the strip still narrates something.
      if (
        activeMarketId &&
        payload.market_id &&
        payload.market_id !== activeMarketId
      ) {
        return;
      }
      // In an active trade the whale + tint already tell the story —
      // the per-strategy "looking, no signal" chatter just piles noise
      // on top of a position the user already holds. Keep the entry
      // confirmation; drop skip / wait lines while guarding.
      if (mode === 'guarding' && payload.decision !== 'enter') {
        return;
      }
      // Build the line UI-side from structured fields. The worker
      // does emit a ``narrative`` string, but it bakes in strategy
      // codenames (``S1``, ``fib``, …) that mean nothing to end-users —
      // we want a conversational "bot's voice" diary instead.
      const body = buildCoveNarrative(payload, t);
      const time = new Date(env.ts * 1000 || Date.now()).toLocaleTimeString(
        locale,
        { hour: '2-digit', minute: '2-digit', second: '2-digit' },
      );
      const tone: TickRow['tone'] =
        payload.decision === 'enter'
          ? 'positive'
          : payload.decision === 'skip'
            ? 'negative'
            : 'neutral';
      // Dedup on the rendered line itself (+ market). Several near-miss
      // gates collapse onto one shared phrase, so two ticks with a
      // different `gate` but identical visible text must still group —
      // keying on `gate` would split them into duplicate-looking rows.
      const key = `${payload.market_id ?? ''}|${body}`;
      const seq = (seqRef.current += 1);
      setTicks((prev) => {
        // Dedup against the WHOLE log, not just the head row. Several
        // strategies tick every ~3 s and interleave, so an identical
        // line is rarely the *consecutive* previous row — it sits one
        // or two rows down. Find it anywhere → bump time + count in
        // place (keeping its id + slot, so React reuses the DOM node:
        // no re-animation, no jumpy reorder). In the steady state — a
        // couple of strategies each repeating their "no signal" line —
        // the log settles to a fixed set of rows whose only motion is
        // the ×N counter ticking up.
        const idx = prev.findIndex((r) => r.dedupeKey === key);
        if (idx !== -1) {
          const next = prev.slice();
          next[idx] = { ...next[idx], time, seq, count: next[idx].count + 1 };
          return next;
        }
        const next = [
          { id: idRef.current++, time, body, tone, dedupeKey: key, count: 1, seq },
          ...prev,
        ];
        if (next.length <= MAX_TICKS) return next;
        // Overflow: evict the least-recently-active row by ``seq``, not
        // the array tail. An in-place dedup refreshes a row's seq
        // without moving it, so the tail can hold a recent line — a
        // blind ``.slice(0, MAX_TICKS)`` would drop it and reset its ×N.
        let oldest = 0;
        for (let i = 1; i < next.length; i += 1) {
          if (next[i].seq < next[oldest].seq) oldest = i;
        }
        return next.filter((_, i) => i !== oldest);
      });
    },
  );

  // Switching market chips — or the UI language — swaps the whole
  // context: the previous rows would linger as stale text (a market
  // the user is no longer looking at, or the pre-switch language).
  // Wipe the log; new ticks refill it in the right market/language.
  // Same-ref return when already empty so React bails the re-render.
  useEffect(() => {
    setTicks((prev) => (prev.length ? [] : prev));
  }, [activeMarketId, i18n.language]);

  // GC: drop scoreByMarket entries that haven't been refreshed in
  // ``SCORE_STALE_MS``. Without this, a market that fell quiet (engine
  // paused on it, network drop) would keep the whale pinned at the
  // last-seen depth forever — better to fall back to the inert default
  // and let the user know the data is stale by visual reset.
  useEffect(() => {
    const id = window.setInterval(() => {
      const cutoff = Date.now() - SCORE_STALE_MS;
      setScoreByMarket((prev) => {
        const next: Record<string, ScoreEntry> = {};
        let dropped = false;
        for (const [marketId, entry] of Object.entries(prev)) {
          if (entry.ts > cutoff) next[marketId] = entry;
          else dropped = true;
        }
        return dropped ? next : prev;
      });
    }, SCORE_GC_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  // GC: bubbles auto-remove from DOM after the CSS animation finishes
  // (2.2s) — but the state list keeps them visible until React re-
  // renders. Trim aggressively so we don't pile up stale entries in
  // background-tab cases where animationend never fires.
  useEffect(() => {
    if (bubbles.length === 0) return;
    const id = window.setTimeout(() => {
      setBubbles((prev) => prev.slice(-MAX_BUBBLES));
    }, 2500);
    return () => window.clearTimeout(id);
  }, [bubbles]);

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // Hunting confidence: prefer the latest stored score_ratio for the
  // active market (from :bot ticks). Falls back to the ``confidence``
  // prop default when (a) no market is selected, (b) no tick has
  // landed yet, or (c) the cached entry was GC'd as stale.
  const liveConfidence =
    activeMarketId && scoreByMarket[activeMarketId] !== undefined
      ? scoreByMarket[activeMarketId].ratio
      : confidence;

  // Depth: hunting reads `liveConfidence` (1 → surface). Sleeping
  // sinks. Guarding mixes side + PnL — when the trade is winning
  // (or neutral) the whale moves in the direction of the bet (UP →
  // surface, DOWN → seabed). When losing, it flips against the bet.
  const depthPct =
    mode === 'sleeping'
      ? 78
      : mode === 'guarding'
        ? guardDepth(guardSide ?? 'up', guardTone)
        : Math.max(14, Math.min(80, (1 - liveConfidence) * 78));

  return (
    <div className={clsx('mt-3', className)} data-testid="ocean-strip">
      <div
        className="relative h-[112px] overflow-hidden rounded-pb-md"
        style={{
          background:
            'linear-gradient(180deg, color-mix(in oklab, var(--pb-accent) 5%, transparent), color-mix(in oklab, var(--pb-accent) 14%, transparent))',
        }}
        aria-hidden
      >
        <Wave reduced={reduced} />

        <div
          className={clsx(
            'absolute transition-[top] duration-700 ease-out',
            !reduced && mode !== 'sleeping' && 'pb-kit-bob',
          )}
          style={{ left: '22%', top: `${depthPct}%` }}
          data-testid="ocean-whale"
        >
          <WhaleGlyph
            size={40}
            className={clsx(
              mode === 'sleeping' && 'opacity-50 grayscale',
              mode === 'guarding' &&
                guardTone === 'positive' &&
                '[--pb-accent:var(--pb-success)]',
              mode === 'guarding' &&
                guardTone === 'negative' &&
                '[--pb-accent:var(--pb-danger)]',
            )}
          />
        </div>

        {!reduced &&
          bubbles.map((b) => (
            <span
              key={b.id}
              className="pb-bubble pointer-events-none absolute bottom-1 h-1.5 w-1.5 rounded-full"
              style={{
                left: `${b.x}%`,
                backgroundColor:
                  b.tint === 'positive'
                    ? 'var(--pb-success)'
                    : b.tint === 'negative'
                      ? 'var(--pb-danger)'
                      : 'var(--pb-accent)',
              }}
              data-testid="ocean-bubble"
            />
          ))}

        {!reduced && pingId > 0 ? (
          <span
            key={`ping-${pingId}`}
            className="pb-pulse pointer-events-none absolute h-3 w-3 rounded-full"
            style={{
              top: `calc(${depthPct}% + 6px)`,
              left: '30%',
              backgroundColor: 'var(--pb-accent)',
              opacity: 0.6,
            }}
            data-testid="ocean-ping"
          />
        ) : null}
      </div>

      <ul className="mt-2 space-y-0.5" data-testid="ocean-ticklog">
        {ticks.length === 0 ? (
          // Suppress the "Pod слушает рынок…" placeholder once the user
          // is in an active trade — the strip's whale + tint already
          // communicate state; an extra "we're listening" line is just
          // chrome there. The line returns for hunting / sleeping where
          // it actually adds context.
          mode === 'guarding' ? null : (
            <li className="pb-caption text-[11px] text-tg-hint">
              {t('cove.ticklog_placeholder')}
            </li>
          )
        ) : (
          ticks.map((row) => (
            <li
              key={row.id}
              className="pb-caption pb-tick-in flex gap-1.5 text-[11px] tabular-nums"
            >
              <span className="text-tg-hint">{row.time}</span>
              <span className="text-tg-hint">·</span>
              <span
                className={clsx(
                  row.tone === 'positive' && 'text-pb-success',
                  row.tone === 'negative' && 'text-pb-danger',
                )}
              >
                {row.tone === 'positive' ? '✓ ' : ''}
                {row.body}
                {row.count > 1 ? ` ×${row.count}` : ''}
              </span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

/**
 * Guarding-mode depth: 14% (near surface) or 74% (near seabed).
 *
 *  side  PnL    direction (matches the bet?)  depth
 *  ----  ----   ----------------------------  ------
 *  UP    +      yes — riding the rally        surface
 *  UP    −      no  — bet's failing           seabed
 *  DOWN  +      yes — drop confirming         seabed
 *  DOWN  −      no  — drop reversing on you   surface
 *  any   neutral — default to bet direction   per-side
 */
function guardDepth(side: 'up' | 'down', tone: GuardTone): number {
  const surface = 14;
  const seabed = 74;
  if (tone === 'negative') {
    return side === 'up' ? seabed : surface;
  }
  // positive or neutral — follow the bet's direction.
  return side === 'up' ? surface : seabed;
}

function Wave({ reduced }: { reduced: boolean }) {
  return (
    <svg
      className={clsx(
        'absolute left-0 top-1 h-3 w-[200%] text-pb-accent',
        !reduced && 'pb-wave',
      )}
      viewBox="0 0 800 12"
      preserveAspectRatio="none"
      aria-hidden
    >
      <path
        d="M0,6 Q100,1 200,6 T400,6 T600,6 T800,6 V12 H0 Z"
        fill="currentColor"
        opacity="0.28"
      />
    </svg>
  );
}
